import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, describeError } from '../lib/api';
import { Button } from '../components/ui';
import { Field, Input, Notice } from '../components/form';
import { Turnstile } from '../components/Turnstile';

/**
 * 忘记口令。
 *
 * 界面文案刻意不承诺「邮件已发出」：服务端对不存在的邮箱也会返回成功，
 * 这里只说「如果账号存在，我们会发送重置链接」。
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ sent: boolean; devResetUrl?: string }>(
        '/api/auth/password/forgot',
        { email: email.trim(), turnstileToken },
      );
      setSent(true);
      setDevResetUrl(result.devResetUrl ?? null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-[420px] space-y-4">
        <h1 className="text-[28px] font-semibold text-fg">找回口令</h1>
        <p className="text-[13px] text-fg-muted">
          输入注册邮箱，我们会发送一封包含重置链接的邮件。链接 30 分钟内有效。
        </p>

        {error ? <Notice tone="danger">{error}</Notice> : null}

        {sent ? (
          <>
            <Notice tone="info">
              如果该邮箱已注册，我们会发送重置链接。请检查收件箱与垃圾邮件目录。
            </Notice>
            {devResetUrl ? (
              <a
                href={devResetUrl}
                className="block break-all rounded-control bg-nest-2 px-3 py-2 text-xs text-brand hover:underline"
              >
                开发环境重置链接：{devResetUrl}
              </a>
            ) : null}
          </>
        ) : (
          <>
            <Field label="邮箱" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="name@example.com"
                autoComplete="email"
                onEnter={submit}
              />
            </Field>
            <Turnstile onToken={setTurnstileToken} />
            <Button variant="primary" block loading={busy} onClick={submit}>
              发送重置链接
            </Button>
          </>
        )}

        <p className="text-center text-[13px] text-fg-muted">
          <Link to="/login" className="text-brand hover:underline">
            返回登录
          </Link>
        </p>
      </div>
    </div>
  );
}
